import { useEffect } from 'react';
import { FieldName } from 'react-hook-form';

import { Country } from '@dreryk/address-common-api';

import { BindingCreator, commonTranslationKeys, FormContextValues } from '@dreryk/react-common';
import { Select, TestId } from '@dreryk/react-components';

import { CountriesSelectList, getCountryDisplayValue } from '~libs.desktop.shared.fields';

import { GuardianAddressFieldName } from './guardianSection.types';

export type GuardianAddressCountryFormBeanPart<FIELD_NAME extends GuardianAddressFieldName> = {
   [key in FIELD_NAME]: {
      country?: Country | null;
   };
};

export type CountryFieldProps<FIELD_NAME extends GuardianAddressFieldName> = Pick<
   FormContextValues<GuardianAddressCountryFormBeanPart<FIELD_NAME>>,
   'watch' | 'trigger'
> & {
   readonly register: BindingCreator<GuardianAddressCountryFormBeanPart<FIELD_NAME>>;
   readonly guardianAddressFieldName: FIELD_NAME;
   readonly testId: TestId;
};

export const CountryField = <FIELD_NAME extends GuardianAddressFieldName>(
   props: CountryFieldProps<FIELD_NAME>
): JSX.Element => {
   const countryFieldName = `${props.guardianAddressFieldName}.country` as const;
   const postcodeFieldName = `${props.guardianAddressFieldName}.postcode` as const;
   const { country, postcode } = props.watch([countryFieldName, postcodeFieldName]) as any;
   useEffect(() => {
      if (postcode) {
         props.trigger(postcodeFieldName as FieldName<GuardianAddressCountryFormBeanPart<FIELD_NAME>>);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [country]);
   return (
      <Select<Country>
         testId={`${props.testId}Country`}
         triggerLabelExtractor={getCountryDisplayValue}
         placeholder={commonTranslationKeys.choose}
         controller={props.register(
            `${props.guardianAddressFieldName}.country` as FieldName<GuardianAddressCountryFormBeanPart<FIELD_NAME>>,
            { required: true }
         )}
         label={commonTranslationKeys.addressData.country}
         defaultValue={Country.of('PL')}
         maxHeight={190}
         searchInputEnabled
      >
         <CountriesSelectList />
      </Select>
   );
};
